<script setup lang="ts">
/**
 * Step 5 — Share capital + allotment + group shareholdings.
 * Mirrors the SHARES block in test_incorporation.html.
 */
import { computed } from 'vue';
import SgEfDocUpload from '../components/SgEfDocUpload.vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import type {
  EfAllotLine,
  EfGroupLine,
  EfPositionHolder,
  EfShareClass,
  EfShareCurrency,
  EfShareGroup,
} from '../composables/incorporationEfilingModel';
import type { EfUploadedFile } from '../composables/useIncorporationEfiling';

const props = defineProps<{
  currencies: EfShareCurrency[];
  allotLines: EfAllotLine[];
  groups: EfShareGroup[];
  holders: EfPositionHolder[];
  uploadedFiles: EfUploadedFile[];
  readonly?: boolean;
}>();

// 배정(B)·그룹(C) Currency 는 Capital(A) 에서 정의된 통화만 노출한다.
const capitalCurrencies = computed<string[]>(() =>
  Array.from(
    new Set((props.currencies || []).map((c) => String(c.currency || '').trim()).filter(Boolean)),
  ),
);
const PAYABLE = [
  { title: 'CASH — full cash', value: 'CASH' },
  { title: 'PARTIAL — partly non-cash', value: 'PARTIAL' },
  { title: 'NONCASH — full non-cash', value: 'NONCASH' },
  { title: 'NONE — unpaid', value: 'NONE' },
];
const ALLOTMENT_METHOD = [
  { title: '(none)', value: '' },
  { title: 'WC — contract in writing', value: 'WC' },
  { title: 'NWC — contract not in writing', value: 'NWC' },
  { title: 'CONST — constitution', value: 'CONST' },
  { title: 'SATISDIV — satisfaction of dividend', value: 'SATISDIV' },
  { title: 'COURT — order of court', value: 'COURT' },
];
const SHARE_TYPES = [
  { title: 'ORD — ordinary', value: 'ORD' },
  { title: 'PREF — preference', value: 'PREF' },
  { title: 'OTH — other', value: 'OTH' },
];
// 배정(B)·그룹(C) 의 Type 은 해당 라인의 통화에 대해 Capital(A) 에서 정의된 주식종류만 노출.
function typesForCurrency(currency: string): typeof SHARE_TYPES {
  const cur = String(currency || '').trim();
  const set = new Set<string>();
  (props.currencies || []).forEach((c) => {
    if (String(c.currency || '').trim() === cur) {
      (c.shareClasses || []).forEach((k) => {
        if (k.shareType) set.add(k.shareType);
      });
    }
  });
  return SHARE_TYPES.filter((t) => set.has(t.value));
}

function needsMethod(payable: string): boolean {
  return payable !== 'CASH' && payable !== 'NONE';
}

const shareholderOptions = () =>
  props.holders
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => h.positions.includes('LC_SH'))
    .map(({ h, i }) => ({ title: h.name || `#${i}`, value: i }));

function addCurrency() {
  props.currencies.push({
    currency: 'SGD',
    payableMethod: 'CASH',
    allotmentMethod: '',
    allotmentMethodDesc: '',
    shareClasses: [],
    documents: [],
  });
}
function removeCurrency(i: number) {
  props.currencies.splice(i, 1);
}
/** 자본 주식종류 드롭다운 — 같은 통화에서 이미 쓴 종류(자기 자신 제외)는 제외 */
function classTypeItems(cur: EfShareCurrency, selfIndex: number): typeof SHARE_TYPES {
  const used = new Set<string>();
  cur.shareClasses.forEach((k, idx) => {
    if (idx !== selfIndex && k.shareType) used.add(k.shareType);
  });
  return SHARE_TYPES.filter((t) => !used.has(t.value));
}
function canAddClass(cur: EfShareCurrency): boolean {
  const used = new Set(cur.shareClasses.map((k) => k.shareType).filter(Boolean));
  return used.size < SHARE_TYPES.length;
}
function addClass(cur: EfShareCurrency) {
  const used = new Set(cur.shareClasses.map((k) => k.shareType));
  const next = SHARE_TYPES.find((t) => !used.has(t.value));
  if (!next) return;
  cur.shareClasses.push({
    shareType: next.value,
    issuedCapAmount: '',
    paidupCapAmount: '',
    shareAllotted: '',
    subClasses: [],
  });
}
function removeClass(cur: EfShareCurrency, i: number) {
  cur.shareClasses.splice(i, 1);
}
function addSub(cls: EfShareClass) {
  cls.subClasses.push({ subclassName: '', shareAllotted: '' });
}
function removeSub(cls: EfShareClass, i: number) {
  cls.subClasses.splice(i, 1);
}

// ── 배정(B): 주주 → 통화 → 클래스 그룹 뷰 (flat allotLines 모델 유지) ──
const allotShareholders = computed(() =>
  props.holders.map((h, i) => ({ h, i })).filter(({ h }) => h.positions.includes('LC_SH')),
);
function holderDisplayName(idx: number): string {
  const h = props.holders[idx];
  if (!h) return `#${idx}`;
  return `${h.name || '#' + idx}${h.regNo ? '-' + h.regNo : ''}`;
}
/** 이 주주에게 배정된 통화 목록(순서 유지) */
function currenciesForHolder(holderIndex: number): string[] {
  const out: string[] = [];
  props.allotLines.forEach((l) => {
    if (l.shareHolderIndex === holderIndex && l.currency && !out.includes(l.currency)) {
      out.push(l.currency);
    }
  });
  return out;
}
/** 주주+통화 에 해당하는 배정 라인 {l, i} */
function linesFor(holderIndex: number, currency: string): { l: EfAllotLine; i: number }[] {
  const res: { l: EfAllotLine; i: number }[] = [];
  props.allotLines.forEach((l, i) => {
    if (l.shareHolderIndex === holderIndex && l.currency === currency) res.push({ l, i });
  });
  return res;
}
function newAllotLine(holderIndex: number, currency: string, shareType?: string): EfAllotLine {
  return {
    shareHolderIndex: holderIndex,
    currency,
    shareType: shareType || typesForCurrency(currency)[0]?.value || 'ORD',
    paidupCapAmount: '',
    shareAllotted: '',
    heldInTrust: false,
    provideNameOfTrust: false,
    nameOfTrust: '',
    subClasses: [],
  };
}
/** 배정 그룹의 Type 드롭다운 — 같은 주주·통화에서 이미 쓴 종류(자기 라인 제외)는 제외 */
function allotTypeItems(
  holderIndex: number,
  currency: string,
  selfIndex: number,
): typeof SHARE_TYPES {
  const used = new Set<string>();
  linesFor(holderIndex, currency).forEach(({ l, i }) => {
    if (i !== selfIndex && l.shareType) used.add(l.shareType);
  });
  return typesForCurrency(currency).filter((t) => !used.has(t.value));
}
/** 이 주주·통화에 아직 안 쓴 종류가 있어 클래스를 더 추가할 수 있는지 */
function canAddAllotClass(holderIndex: number, currency: string): boolean {
  const used = new Set(linesFor(holderIndex, currency).map(({ l }) => l.shareType).filter(Boolean));
  return typesForCurrency(currency).some((t) => !used.has(t.value));
}
/** 특정 주주·통화에 클래스(배정 라인) 추가 — 아직 안 쓴 종류를 기본값으로 */
function addAllotClass(holderIndex: number, currency: string) {
  const used = new Set(linesFor(holderIndex, currency).map(({ l }) => l.shareType));
  const next = typesForCurrency(currency).find((t) => !used.has(t.value));
  props.allotLines.push(newAllotLine(holderIndex, currency, next?.value));
}
/** 특정 주주에게 새 통화 배정 그룹 추가(아직 안 쓴 capital 통화 우선) */
function addAllotCurrency(holderIndex: number) {
  const used = currenciesForHolder(holderIndex);
  const cur =
    capitalCurrencies.value.find((c) => !used.includes(c)) || capitalCurrencies.value[0] || '';
  props.allotLines.push(newAllotLine(holderIndex, cur));
}
/** 통화 그룹 전체의 통화 변경 */
function changeGroupCurrency(holderIndex: number, oldCur: string, newCur: string) {
  if (!newCur || newCur === oldCur) return;
  props.allotLines.forEach((l) => {
    if (l.shareHolderIndex === holderIndex && l.currency === oldCur) l.currency = newCur;
  });
}
function removeAllotLine(i: number) {
  props.allotLines.splice(i, 1);
}
function addAllotSub(l: EfAllotLine) {
  l.subClasses.push({ subclassName: '', shareAllotted: '' });
}
function removeAllotSub(l: EfAllotLine, i: number) {
  l.subClasses.splice(i, 1);
}

function addGroup() {
  props.groups.push({ groupName: '', shareHolderIndices: [], lines: [] });
}
function removeGroup(gi: number) {
  props.groups.splice(gi, 1);
}
function addGroupLine(g: EfShareGroup) {
  g.lines.push({
    currency: props.currencies[0]?.currency || 'SGD',
    shareType: 'ORD',
    paidupCapAmount: '',
    shareAllotted: '',
    heldInTrust: false,
    provideNameOfTrust: false,
    nameOfTrust: '',
    subClasses: [],
  });
}
function removeGroupLine(g: EfShareGroup, li: number) {
  g.lines.splice(li, 1);
}
function addGroupSub(l: EfGroupLine) {
  l.subClasses.push({ subclassName: '', shareAllotted: '' });
}
function removeGroupSub(l: EfGroupLine, i: number) {
  l.subClasses.splice(i, 1);
}

// 서브클래스 수량 합계는 상위(주식종류/배정선/그룹라인)의 shareAllotted 를 초과할 수 없다.
function sumSubShares(subs?: { shareAllotted: string }[]): number {
  return (subs || []).reduce((acc, s) => acc + (Number(s.shareAllotted) || 0), 0);
}
function subOverflow(main: string, subs?: { shareAllotted: string }[]): boolean {
  return (subs || []).length > 0 && sumSubShares(subs) > (Number(main) || 0);
}
const SUB_OVERFLOW_MSG = 'Number of shares cannot be less than total sub-class shares';
function subOverflowMsgs(main: string, subs?: { shareAllotted: string }[]): string[] {
  return subOverflow(main, subs) ? [SUB_OVERFLOW_MSG] : [];
}

// 밸런스: 자본 총량(통화|종류) == 개별 배정선 + 그룹 배정 합계. 불일치 키를 계산해
// 해당 통화/종류의 shareAllotted 필드(자본·배정선·그룹)를 모두 빨갛게 표시한다.
const BALANCE_MSG = 'Allotted total (incl. group) must equal the share capital for this currency/type';
const balanceMismatchKeys = computed<Set<string>>(() => {
  const cap: Record<string, number> = {};
  props.currencies.forEach((c) =>
    c.shareClasses.forEach((k) => {
      const key = `${c.currency}|${k.shareType}`;
      cap[key] = (cap[key] || 0) + (Number(k.shareAllotted) || 0);
    }),
  );
  const alloc: Record<string, number> = {};
  props.allotLines.forEach((l) => {
    const key = `${l.currency}|${l.shareType}`;
    alloc[key] = (alloc[key] || 0) + (Number(l.shareAllotted) || 0);
  });
  props.groups.forEach((g) =>
    g.lines.forEach((l) => {
      const key = `${l.currency}|${l.shareType}`;
      alloc[key] = (alloc[key] || 0) + (Number(l.shareAllotted) || 0);
    }),
  );
  const bad = new Set<string>();
  new Set([...Object.keys(cap), ...Object.keys(alloc)]).forEach((key) => {
    if ((cap[key] || 0) !== (alloc[key] || 0)) bad.add(key);
  });
  return bad;
});
function balanceBad(currency: string, shareType: string): boolean {
  return balanceMismatchKeys.value.has(`${currency}|${shareType}`);
}
/** 이 shareAllotted 필드에 표시할 에러(서브클래스 초과 + 밸런스 불일치) */
function allotErrors(
  currency: string,
  shareType: string,
  main: string,
  subs?: { shareAllotted: string }[],
): string[] {
  const errs = subOverflowMsgs(main, subs);
  if (balanceBad(currency, shareType)) errs.push(BALANCE_MSG);
  return errs;
}
</script>

<template>
  <div class="ef-step">
    <!-- A. Share capital -->
    <div class="ef-step__section">
      <div class="ef-step__h">A. Share capital (by currency)</div>
      <div v-if="!currencies.length" class="ef-step__empty">No currency capital added.</div>

      <v-card
        v-for="(cur, ci) in currencies"
        :key="ci"
        variant="outlined"
        class="mb-3 pa-3"
      >
        <v-row dense>
          <v-col cols="12" sm="4">
            <CmBaseCodeAutocomplete
              v-model="cur.currency"
              main-code="B08"
              label="Currency"
              :read-only="readonly"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="cur.payableMethod"
              :items="PAYABLE"
              label="Payable method"
              density="compact"
              variant="outlined"
              hide-details
              :readonly="readonly"
            />
          </v-col>
          <v-col v-if="needsMethod(cur.payableMethod)" cols="12" sm="4">
            <v-select
              v-model="cur.allotmentMethod"
              :items="ALLOTMENT_METHOD"
              label="Method of allotment"
              density="compact"
              variant="outlined"
              hide-details
              :readonly="readonly"
            />
          </v-col>
          <v-col v-if="needsMethod(cur.payableMethod)" cols="12">
            <v-text-field
              v-model="cur.allotmentMethodDesc"
              label="Allotment method description"
              density="compact"
              variant="outlined"
              hide-details
              :readonly="readonly"
            />
          </v-col>
        </v-row>

        <!-- allotment method doc (non-cash) -->
        <div v-if="needsMethod(cur.payableMethod) || cur.documents.length" class="mt-2">
          <div class="ef-step__sub">Allotment method documents</div>
          <SgEfDocUpload
            :docs="cur.documents"
            :max-count="3"
            :readonly="readonly"
            :uploaded-files="uploadedFiles"
            hint="Attach or replace the contract / court order PDF."
          />
        </div>

        <!-- share classes -->
        <div class="ef-step__sub mt-2">Share classes</div>
        <div v-for="(cls, li) in cur.shareClasses" :key="li" class="sc-class">
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-select
                v-model="cls.shareType"
                :items="classTypeItems(cur, li)"
                label="Share type"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="cls.issuedCapAmount"
                label="Issued cap"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="cls.paidupCapAmount"
                label="Paid-up cap"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="cls.shareAllotted"
                label="Share allotted"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :error-messages="allotErrors(cur.currency, cls.shareType, cls.shareAllotted, cls.subClasses)"
                :readonly="readonly"
              />
            </v-col>
          </v-row>
          <!-- subclasses -->
          <div v-for="(sub, si) in cls.subClasses" :key="si" class="sc-sub">
            <v-text-field
              v-model="sub.subclassName"
              label="Subclass"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 160px"
              :readonly="readonly"
            />
            <v-text-field
              v-model="sub.shareAllotted"
              label="Allotted"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 160px"
              :readonly="readonly"
            />
            <v-btn
              v-if="!readonly"
              icon="mdi-close"
              size="x-small"
              variant="text"
              @click="removeSub(cls, si)"
            />
          </div>
          <div v-if="!readonly" class="sc-class-actions">
            <v-btn variant="text" size="x-small" class="text-none" @click="addSub(cls)">
              + Subclass
            </v-btn>
            <v-btn
              variant="text"
              size="x-small"
              color="error"
              class="text-none"
              @click="removeClass(cur, li)"
            >
              Remove class
            </v-btn>
          </div>
        </div>

        <div v-if="!readonly" class="mt-1">
          <v-btn
            variant="text"
            size="small"
            class="text-none"
            :disabled="!canAddClass(cur)"
            @click="addClass(cur)"
          >
            + Share class
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            color="error"
            class="text-none"
            @click="removeCurrency(ci)"
          >
            Remove currency
          </v-btn>
        </div>
      </v-card>

      <v-btn
        v-if="!readonly"
        variant="tonal"
        color="primary"
        size="small"
        class="text-none"
        prepend-icon="mdi-plus"
        @click="addCurrency"
      >
        Add currency capital
      </v-btn>
    </div>

    <!-- B. Allotment (grouped: shareholder → currency → class of shares) -->
    <div class="ef-step__section">
      <div class="ef-step__h">B. Share allotment (per shareholder)</div>
      <div v-if="!allotShareholders.length" class="ef-step__empty">No shareholders.</div>

      <v-card
        v-for="({ i: hIdx }, n) in allotShareholders"
        :key="hIdx"
        variant="outlined"
        class="mb-3 pa-3"
      >
        <div class="al-holder-name">{{ n + 1 }}. {{ holderDisplayName(hIdx) }}</div>

        <div v-for="cur in currenciesForHolder(hIdx)" :key="cur" class="al-cur-group">
          <v-select
            :model-value="cur"
            :items="capitalCurrencies"
            label="Currency"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 220px"
            class="mb-2"
            :readonly="readonly"
            @update:model-value="changeGroupCurrency(hIdx, cur, $event)"
          />

          <div v-for="{ l, i } in linesFor(hIdx, cur)" :key="i" class="al-class">
            <v-row dense>
              <v-col cols="6" sm="3">
                <v-select
                  v-model="l.shareType"
                  :items="allotTypeItems(hIdx, cur, i)"
                  label="Class of shares"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="l.shareAllotted"
                  label="Number of shares"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :error-messages="allotErrors(l.currency, l.shareType, l.shareAllotted, l.subClasses)"
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="l.paidupCapAmount"
                  label="Paid-up share capital"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
            </v-row>
            <div class="al-trust">
              <v-checkbox
                v-model="l.heldInTrust"
                label="Held in trust"
                density="compact"
                hide-details
                :disabled="readonly"
              />
              <v-checkbox
                v-model="l.provideNameOfTrust"
                label="Provide name of trust"
                density="compact"
                hide-details
                :disabled="readonly"
              />
              <v-text-field
                v-if="l.provideNameOfTrust"
                v-model="l.nameOfTrust"
                label="Name of trust"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </div>
            <div v-for="(sub, si) in l.subClasses" :key="si" class="sc-sub">
              <v-text-field
                v-model="sub.subclassName"
                label="Subclass"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 160px"
                :readonly="readonly"
              />
              <v-text-field
                v-model="sub.shareAllotted"
                label="Number of shares"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 160px"
                :readonly="readonly"
              />
              <v-btn
                v-if="!readonly"
                icon="mdi-close"
                size="x-small"
                variant="text"
                @click="removeAllotSub(l, si)"
              />
            </div>
            <div v-if="!readonly" class="al-actions">
              <v-btn variant="text" size="x-small" class="text-none" @click="addAllotSub(l)">
                + Sub-class
              </v-btn>
              <v-btn
                variant="text"
                size="x-small"
                color="error"
                class="text-none"
                @click="removeAllotLine(i)"
              >
                Delete class
              </v-btn>
            </div>
          </div>

          <v-btn
            v-if="!readonly"
            variant="text"
            size="small"
            class="text-none"
            :disabled="!canAddAllotClass(hIdx, cur)"
            @click="addAllotClass(hIdx, cur)"
          >
            + Add class of shares
          </v-btn>
        </div>

        <v-btn
          v-if="!readonly && capitalCurrencies.length"
          variant="tonal"
          color="primary"
          size="small"
          class="text-none mt-2"
          prepend-icon="mdi-plus"
          @click="addAllotCurrency(hIdx)"
        >
          Add currency allotment
        </v-btn>
      </v-card>
    </div>

    <!-- C. Group shareholdings (editable) -->
    <div class="ef-step__section">
      <div class="ef-step__h">C. Group shareholdings</div>
      <div v-if="!groups.length" class="ef-step__empty">No group shareholdings.</div>

      <v-card
        v-for="(g, gi) in groups"
        :key="gi"
        variant="outlined"
        class="mb-2 pa-3"
      >
        <v-row dense>
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="g.groupName"
              label="Group name"
              density="compact"
              variant="outlined"
              maxlength="3"
              counter="3"
              hint="Max 3 characters"
              :readonly="readonly"
            />
          </v-col>
          <v-col cols="12" sm="7">
            <v-select
              v-model="g.shareHolderIndices"
              :items="shareholderOptions()"
              label="Holders"
              multiple
              chips
              closable-chips
              density="compact"
              variant="outlined"
              hide-details
              :readonly="readonly"
            />
          </v-col>
        </v-row>

        <div v-for="(l, li) in g.lines" :key="li" class="grp-line-edit">
          <v-row dense>
            <v-col cols="6" sm="3">
              <v-select
                v-model="l.currency"
                :items="capitalCurrencies"
                label="Currency"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="2">
              <v-select
                v-model="l.shareType"
                :items="typesForCurrency(l.currency)"
                label="Type"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="l.shareAllotted"
                label="Allotted"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :error-messages="allotErrors(l.currency, l.shareType, l.shareAllotted, l.subClasses)"
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model="l.paidupCapAmount"
                label="Paid-up share capital"
                density="compact"
                variant="outlined"
                hide-details
                :readonly="readonly"
              />
            </v-col>
          </v-row>
          <div class="al-trust">
            <v-checkbox
              v-model="l.heldInTrust"
              label="Held in trust"
              density="compact"
              hide-details
              :disabled="readonly"
            />
            <v-checkbox
              v-model="l.provideNameOfTrust"
              label="Provide name of trust"
              density="compact"
              hide-details
              :disabled="readonly"
            />
            <v-text-field
              v-if="l.provideNameOfTrust"
              v-model="l.nameOfTrust"
              label="Name of trust"
              density="compact"
              variant="outlined"
              hide-details
              :readonly="readonly"
            />
          </div>
          <!-- group line subclasses -->
          <div v-for="(sub, si) in l.subClasses" :key="si" class="sc-sub">
            <v-text-field
              v-model="sub.subclassName"
              label="Subclass"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 160px"
              :readonly="readonly"
            />
            <v-text-field
              v-model="sub.shareAllotted"
              label="Allotted"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 160px"
              :readonly="readonly"
            />
            <v-btn
              v-if="!readonly"
              icon="mdi-close"
              size="x-small"
              variant="text"
              @click="removeGroupSub(l, si)"
            />
          </div>
          <div v-if="!readonly" class="sc-class-actions">
            <v-btn variant="text" size="x-small" class="text-none" @click="addGroupSub(l)">
              + Subclass
            </v-btn>
            <v-btn
              variant="text"
              size="x-small"
              color="error"
              class="text-none"
              @click="removeGroupLine(g, li)"
            >
              Remove line
            </v-btn>
          </div>
        </div>

        <div v-if="!readonly" class="mt-1">
          <v-btn variant="text" size="small" class="text-none" @click="addGroupLine(g)">
            + Group line
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            color="error"
            class="text-none"
            @click="removeGroup(gi)"
          >
            Remove group
          </v-btn>
        </div>
      </v-card>

      <v-btn
        v-if="!readonly"
        variant="tonal"
        color="primary"
        size="small"
        class="text-none"
        prepend-icon="mdi-plus"
        @click="addGroup"
      >
        Add group
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.ef-step__section {
  margin-bottom: 18px;
}
.ef-step__h {
  font-size: 14px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 8px;
}
.ef-step__sub {
  font-size: 12.5px;
  font-weight: 700;
  color: #42506a;
  margin: 10px 0 4px;
}
.ef-step__empty {
  font-size: 12.5px;
  color: #8a95a8;
  padding: 8px 0;
}
.sc-class {
  border: 1px dashed #d5dae4;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
}
.sc-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.sc-class-actions {
  margin-top: 4px;
}
.sc-doc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  margin-right: 12px;
  text-decoration: none;
  color: #2563eb;
}
.al-trust {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
.al-actions {
  display: flex;
  justify-content: flex-end;
}
.grp-line-edit {
  border: 1px dashed #d5dae4;
  border-radius: 8px;
  padding: 8px;
  margin-top: 8px;
}
.al-holder-name {
  font-weight: 700;
  font-size: 14px;
  color: #1a2336;
  margin-bottom: 8px;
}
.al-cur-group {
  border-top: 1px solid #eef1f6;
  padding-top: 10px;
  margin-top: 10px;
}
.al-class {
  border: 1px dashed #d5dae4;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
}
</style>
